import { useEffect, useMemo, useRef } from "react";
import { useCompatMember, useCompatOrganization } from "../../../hooks/stytch-compat";
import { resolveCacheApiBase } from "../../../contexts/TenantQueryClientProvider";
import { subscribeConnectivityObservation } from "../../../native/nativeNav";
import { usePlatform } from "../../../platform/PlatformProvider";
import { deliverNextMarkupOutboxOperation, markupOutboxWindowOwner } from "./markupOutboxDelivery";
import { listMarkupOutboxScopesForIdentity, readMarkupOutboxPendingOperations, subscribeMarkupOutboxEnqueue, type MarkupOutboxIdentity, type MarkupOutboxScope } from "./markupOutbox";
import { isMarkupOutboxScopeActive, subscribeMarkupOutboxActiveScopes } from "./markupOutboxReplayRegistry";

/** One per window: retries unmounted durable scopes after auth binds, connectivity evidence,
 * and the earliest persisted backoff. Mounted viewers retain UI-state ownership. A host
 * with no durable outbox (web) has nothing to replay — mounts as a no-op rather than
 * running an IndexedDB-polling loop for a queue that never gets any rows. */
export function MarkupOutboxReplayProvider() {
    const hasDurableOutbox = Boolean(usePlatform().offline?.markupOutbox);
    const { member } = useCompatMember();
    const { organization } = useCompatOrganization();
    const owner = useRef(markupOutboxWindowOwner()).current;
    const apiBase = resolveCacheApiBase();
    const memberId = member?.member_id;
    const organizationId = organization?.organization_id;
    const identity = useMemo<MarkupOutboxIdentity | null>(() => memberId && organizationId && apiBase
        ? { origin: apiBase, organizationId, memberId }
        : null, [apiBase, organizationId, memberId]);
    const identityRef = useRef(identity);
    identityRef.current = identity;
    useEffect(() => {
        if (!hasDurableOutbox || !identity || !identity.origin) return;
        let cancelled = false;
        let running = false;
        let requested = false;
        let timer: ReturnType<typeof setTimeout> | null = null;
        const clearTimer = () => { if (timer) clearTimeout(timer); timer = null; };
        const current = (scope: MarkupOutboxScope) => {
            const live = identityRef.current;
            return !cancelled && !isMarkupOutboxScopeActive(scope) && live?.origin === identity.origin &&
                live.organizationId === identity.organizationId && live.memberId === identity.memberId && resolveCacheApiBase() === identity.origin;
        };
        const drain = async () => {
            if (cancelled) return;
            if (running) { requested = true; return; }
            running = true;
            clearTimer();
            try {
                const scopes = await listMarkupOutboxScopesForIdentity(identity);
                let earliest: number | null = null;
                for (const scope of scopes) {
                    if (!current(scope)) continue;
                    const rows = await readMarkupOutboxPendingOperations(scope);
                    const head = rows[0];
                    if (!head) continue;
                    if (head.state === "failed" || head.state === "conflict") continue;
                    const due = head.state === "sending" ? (head.leaseUntil ?? Date.now()) : head.nextAttemptAt;
                    if (due > Date.now()) { earliest = earliest === null ? due : Math.min(earliest, due); continue; }
                    await deliverNextMarkupOutboxOperation(scope, owner, () => current(scope));
                    const next = (await readMarkupOutboxPendingOperations(scope))[0];
                    if (next && next.state !== "failed" && next.state !== "conflict") {
                        const due = next.state === "sending" ? (next.leaseUntil ?? Date.now()) : next.nextAttemptAt;
                        earliest = earliest === null ? due : Math.min(earliest, due);
                    }
                }
                if (!cancelled && earliest !== null) timer = setTimeout(() => void drain().catch(() => undefined), Math.max(100, earliest - Date.now()));
            } catch {
                if (!cancelled) timer = setTimeout(() => void drain().catch(() => undefined), 4_000);
            } finally {
                running = false;
                if (requested) { requested = false; void drain().catch(() => undefined); }
            }
        };
        void drain().catch(() => undefined);
        const unsubscribeConnectivity = subscribeConnectivityObservation((state) => { if (state === "reachable") void drain().catch(() => undefined); });
        const unsubscribeActive = subscribeMarkupOutboxActiveScopes(() => void drain().catch(() => undefined));
        const unsubscribeEnqueue = subscribeMarkupOutboxEnqueue(() => void drain().catch(() => undefined));
        return () => { cancelled = true; clearTimer(); unsubscribeConnectivity(); unsubscribeActive(); unsubscribeEnqueue(); };
    }, [hasDurableOutbox, identity, owner]);
    return null;
}
